import type { TreeViewSelectNode } from '../../../TreeViewSelect';
import type { DiamRoleDetails } from '../../diamApi';
import { mapRolePermissionTree } from '../RoleDetails/mapRolePermissionTree';
import type { RolePermissionNode } from '../RoleDetails/types';

/** Keeps MUI Tree View ids unique when multiple functional roles share attribute group keys. */
export function createRoleContentScopePrefix(roleId: string): string {
  return `${roleId}::`;
}

export function scopeCreateRoleContentId(
  roleId: string,
  nodeId: string,
): string {
  return `${createRoleContentScopePrefix(roleId)}${nodeId}`;
}

export function unscopeCreateRoleContentIds(
  roleId: string,
  selectedContents: readonly string[],
): string[] {
  const prefix = createRoleContentScopePrefix(roleId);
  return selectedContents
    .filter(id => id.startsWith(prefix))
    .map(id => id.slice(prefix.length));
}

function mapPermissionNodes(
  nodes: readonly RolePermissionNode[],
  roleId: string,
): TreeViewSelectNode[] {
  return nodes.map(node => ({
    id: scopeCreateRoleContentId(roleId, node.id),
    label: node.label,
    ...(node.children?.length
      ? { children: mapPermissionNodes(node.children, roleId) }
      : {}),
  }));
}

export function mapCreateRoleContentsTree(
  roles: readonly DiamRoleDetails[],
): TreeViewSelectNode[] {
  return roles
    .filter(role => role.type === 'FUNCTIONAL')
    .slice()
    .sort((left, right) => left.name.localeCompare(right.name))
    .map(role => {
      const permissionTree = mapRolePermissionTree(role);
      const children = mapPermissionNodes(permissionTree, role.roleId);

      if (children.length === 0) {
        return {
          id: `role:${role.roleId}`,
          label: role.name,
        };
      }

      return {
        id: `group:${role.roleId}`,
        label: role.name,
        children,
      };
    });
}

export function collectFunctionalRoleAttributeIds(
  role: DiamRoleDetails,
): string[] {
  const attributeIds: string[] = [];

  const walk = (nodes: readonly RolePermissionNode[]) => {
    for (const node of nodes) {
      if (node.children?.length) {
        walk(node.children);
        continue;
      }

      if (node.id.startsWith('role:') || node.id.startsWith('group:')) {
        continue;
      }

      attributeIds.push(node.id);
    }
  };

  walk(mapRolePermissionTree(role));
  return attributeIds;
}
